import { MRT_ColumnDef } from 'mantine-react-table';
import { Text } from '@mantine/core';
import type { CSSProperties } from 'react';
import { formatPriceWithoutCurrency } from 'app/utils/util-functions';
import { autoCalculatedFees, paidToOptions } from 'app/models/dropdown-options';
import { MouseEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteSubTradeFacilityFees } from 'app/state/ducks/traded-facilities/thunks';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';

const getTextStyle = (weight: number, lineHeight: string, textAlign?: 'start' | 'end' | 'center'): CSSProperties => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: weight,
    lineHeight: lineHeight,
    textAlign: textAlign ?? 'center',
    color: '#413E3E',
    width: '100%',
});

function TableHead({ header, textAlign }: { header: string, textAlign?: 'start' | 'end' | 'center' }) {
    return (
        <Text style={getTextStyle(700, '22px', textAlign)}>
            {header}
        </Text>
    );
}

// Display convention: the Accruing Fees table always shows a POSITIVE magnitude, and the
// "Paid To" column names the party that actually receives the fee. A fee with a POSITIVE amount
// goes to its stored `paidTo`; a NEGATIVE amount means the fee reverses to the OTHER party
// (Buyer <-> Seller). Flipping on the fee's OWN sign (not the signed contribution) keeps the
// recipient consistent with the Funding Memo: e.g. Delayed Compensation has a positive amount paid
// to the Buyer, so it stays Buyer; a negative Cost of Carry (negative purchase price) flips to Buyer.
const effectivePaidTo = (rawAmount: number, paidTo: string): string => {
    if (rawAmount >= 0) return paidTo
    return paidTo === 'PaidToBuyer' ? 'PaidToSeller' : 'PaidToBuyer'
}

export const columnDefsAccrualFee: MRT_ColumnDef<any>[] = [
    {
        header: 'Type',
        accessorKey: 'type',
        size: 100,
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <TableHead header={'Type'} textAlign='start' />
        ),
        Cell: ({ cell }) => {
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'start' }}>
                        {cell.getValue<string>()}
                    </Text>
                </div >
            );
        },
    },
    {
        header: 'Calculated Amount',
        size: 60,
        accessorKey: 'otherAmount',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <TableHead header={'Calculated Amount'} textAlign='end' />
        ),
        Cell: ({ cell, row }) => {

            const overrideAmount = row.original.overrideAmount;
            const otherAmount = cell.getValue<string>()
            const amount = otherAmount && otherAmount !== "" ? otherAmount : overrideAmount;
            // Always show the positive magnitude; the "Paid To" column carries the counterparty
            // (flipped when the signed value is negative). See effectivePaidTo above.
            const formattedAmount = formatPriceWithoutCurrency(Math.abs(Number(amount)), 2, 2);

            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'end' }}>
                        {formattedAmount}
                    </Text>
                </div>
            );
        },
    },
    {
        header: 'Paid To',
        accessorKey: 'paidTo',
        size: 50,
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <TableHead header={'Paid To'} />
        ),
        Cell: ({ row }) => {
            // Show the counterparty for the positive magnitude displayed in the amount columns:
            // when the signed value is negative the fee is shown as positive to the OTHER party.
            const otherAmount = row.original.otherAmount;
            const overrideAmount = row.original.overrideAmount;
            const amount = otherAmount && otherAmount !== "" ? Number(otherAmount) : Number(overrideAmount);
            const displayPaidTo = effectivePaidTo(amount, row.original.paidTo);
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'center' }}>
                        {paidToOptions.find(({ value }) => value === displayPaidTo)?.label ?? ""}
                    </Text>
                </div>
            );
        },
    },
    {
        header: 'Override Amount',
        accessorKey: 'overrideAmount',
        size: 60,
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <TableHead header={'Override Amount'} textAlign='end' />
        ),
        Cell: ({ cell, row }) => {
            const otherAmount = row.original.otherAmount;
            if (!otherAmount) {
                return (
                    <>
                    </>
                );
            }
            const overrideAmount = Number(cell.getValue<string>());
            // Always show the positive magnitude; direction is carried by the "Paid To" column.
            const formattedAmount = formatPriceWithoutCurrency(Math.abs(overrideAmount), 2, 2);
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'end', color: '#345591' }}>
                        {formattedAmount}
                    </Text>
                </div>
            );
        },
    },
    {
        header: 'Reason',
        accessorKey: 'reason',
        size: 350,
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <TableHead header={'Reason'} textAlign='start' />
        ),
        Cell: ({ cell }) => {
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px'), textAlign: 'start' }}>
                        {cell.getValue<string>()}
                    </Text>
                </div>
            );
        },
    },
    {
        header: 'Delete',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <></>
        ),
        enableColumnFilter: false,
        enableColumnActions: false,
        size: 50,
        Cell: ({ row }) => {
            if (!row.original.isEditable) {
                return <></>
            }
            const [showDeleteModal, setShowDeleteModal] = useState(false);

            const dispatch = useDispatch()

            const handleDelete = async () => {
                const response: any = await dispatch(deleteSubTradeFacilityFees(row.original.facilityId, row.original.subTradeId, row.original.type));
                if (response?.success) {
                    SuccessNotification({ title: 'Deleted Fee' });
                } else {
                    ErrorNotification({
                        title: 'Delete Fee Failed',
                        message: response.payload ?? 'Check data and try again',
                    });
                }
                setShowDeleteModal(false);
            };
            return !autoCalculatedFees.includes(row.original.type) ? (
                <>
                    <Text
                        onClick={(event: MouseEvent<HTMLParagraphElement>) => {
                            event.stopPropagation();
                            setShowDeleteModal(true)
                        }}
                        style={{ ...getTextStyle(700, '22px'), cursor: 'pointer', color: 'red' }}
                    >
                        Delete
                    </Text >
                    {
                        showDeleteModal && (
                            <DeleteConfirmation
                                onConfirm={() => handleDelete()}
                                onCancel={() => setShowDeleteModal(false)}
                            />
                        )
                    }
                </>
            )
                : <></>

        },
    }


];
